import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Box, CircularProgress, Tooltip, Tabs, Tab } from "@mui/material";
import styles from "./fgDemand.module.css";
import Filter from "Samarth Module/components/FGDemand/filter/filter";
import Dropdown from "Samarth Module/components/FGDemand/dropdown";
import DemandTable, { exportDemandTable } from "Samarth Module/components/FGDemand/demandTable/demandTable";
import ProductVisionTable, { exportProductVisionTable } from "Samarth Module/components/FGDemand/productVisionTable/productVisionTable";
import XLSIcon from '../../../assets/images/xls-icon.svg'
import { getColumnPreferences } from "Samarth Module/api/api";
import styled from "@emotion/styled";

const SITE_CODE_OPTIONS = ["Site Code", "Site Cluster",];
const BG_LEVEL_OPTIONS = ["BG", "Small C", "Format", "Basepack", "CBU", "RMPM"];
const DEFAULT_DEMAND_COLUMNS = [
    { key: "bg", label: "BG", visibility: true, DisplayOrder: 1 },
    { key: "sites", label: "Site", visibility: true, DisplayOrder: 2 },
    { key: "demand", label: "Demand", visibility: true, DisplayOrder: 3 },
    { key: "week1", label: "Week 1", visibility: true, DisplayOrder: 4 },
    { key: "week2", label: "Week 2", visibility: true, DisplayOrder: 5 },
    { key: "week3", label: "Week 3", visibility: true, DisplayOrder: 6 },
    { key: "week4", label: "Week 4", visibility: true, DisplayOrder: 7 },
];
const DEFAULT_PV_COLUMNS = [
    { key: "site", label: "Site", visibility: true, DisplayOrder: 1 },
    { key: "fg", label: "FG", visibility: true, DisplayOrder: 2 },
    { key: "count", label: "Count of Active PV's", visibility: true, DisplayOrder: 3 },
    { key: "active", label: "Active PV's", visibility: true, DisplayOrder: 4 },
    { key: "latestCreated", label: "Latest created PV's", visibility: true, DisplayOrder: 5 },
    { key: "latestUsed", label: "Latest used PV's", visibility: true, DisplayOrder: 6 },
];

const getUserEmail = () => {
    try {
        return JSON.parse(localStorage.getItem("UserInfo"))?.user_info?.email || "";
    } catch (error) {
        console.error("Failed to parse UserInfo from localStorage:", error);
        return "";
    }
};

const TalViewTabs = styled(Tabs)({
    "&.MuiTabs-root": { minHeight: "0px" },
    "&.MuiTabs-root .MuiTabs-fixed span": { background: "none" },
});

const TalViewTab = styled(Tab)({
    "&.MuiButtonBase-root": {
        fontSize: "10px",
        fontWeight: 600,
        lineHeight: 1,
        border: "1px solid #949495",
        padding: "8px 12px",
        minHeight: "0px",
        background: "#fff",
        textTransform: "capitalize",
        color: "#333",
    },
    "&.MuiButtonBase-root: first-of-type": {
        borderRadius: "5px 0 0 5px",
    },
    "&.MuiButtonBase-root: last-of-type": {
        borderRadius: "0 5px 5px 0",
    },
    "&.Mui-selected": {
        color: "#fff",
        border: "1px solid #1F36C7",
        background: "#1F36C7",
    },
});

function RmpmDashboard() {
    const [metric, setMetric] = useState("demand");
    const [demandFilters, setDemandFilters] = useState({});
    const [pvFilters, setPvFilters] = useState({});
    const [demandColumns, setDemandColumns] = useState(DEFAULT_DEMAND_COLUMNS);
    const [pvColumns, setPvColumns] = useState(DEFAULT_PV_COLUMNS);
    const [demandColumnsReady, setDemandColumnsReady] = useState(false);
    const [bgLevels, setBgLevels] = useState(BG_LEVEL_OPTIONS);
    const [siteCodeSelection, setSiteCodeSelection] = useState(SITE_CODE_OPTIONS);
    // Picking both (or "All") has to fall back to the same default state as
    // picking neither — there's no combined Site Code + Site Cluster grouping
    // for the table to switch into, so treat it as no selection instead of
    // letting Site Code win by array-order.
    const hasSiteCode = siteCodeSelection.includes("Site Code");
    const hasSiteCluster = siteCodeSelection.includes("Site Cluster");
    const showSiteCode = hasSiteCode === hasSiteCluster
        ? null
        : hasSiteCode;
    const clearFiltersRef = useRef(false);
    const exportStatusTimeoutRef = useRef(null);
    const [exportStatus, setExportStatus] = useState(null);
    const sublevelFilters = useMemo(() => {
        if (!bgLevels.length || bgLevels.length==6) return [];
        // The backend resolves a drill straight to any picked level via
        // `parent.nextLevel` — it doesn't need the skipped levels listed
        // here too, so this only reorders the picks into hierarchy order.
        return BG_LEVEL_OPTIONS
            .filter((level) => bgLevels.includes(level))
            .map((level) => (level === "Small C" ? "SmallC" : level.toUpperCase()));
    }, [bgLevels]);
    const [filterLoading, setFilterLoading] = useState(true);
    const [revealed, setRevealed] = useState(false);
    const [uom, setUom] = useState("Default");
    const isSiteColumnVisible = useMemo(
        () => demandColumns.some((column) => {
            const label = String(column?.label ?? "");
            return (label === "Site Code") && column.visibility !== false;
        }),
        [demandColumns]
    );
    const isBgColumnVisible = useMemo(
        () => demandColumns.some((column) => {
            const label = String(column?.label ?? "");
            return (label==='BG') && column.visibility !== false;
        }),
        [demandColumns]
    );

    const handleFilterLoading = useCallback((value) => setFilterLoading(value), []);

    useEffect(() => {
        if (!revealed && !filterLoading) {
            setRevealed(true);
        }
    }, [revealed, filterLoading]);

    useEffect(() => () => {
        if (exportStatusTimeoutRef.current) clearTimeout(exportStatusTimeoutRef.current);
    }, []);

    useEffect(() => {
        const email = getUserEmail();
        if (!email) {
            setDemandColumnsReady(true);
            return undefined;
        }
        let cancelled = false;
        getColumnPreferences(email, "Demand Page")
            .then((response) => {
                const columns = response?.columns || response?.default_columns;
                if (!cancelled && Array.isArray(columns) && columns.length) {
                    setDemandColumns(columns.map((column, index) => ({
                        ...column,
                        key: column.key || column.label,
                        DisplayOrder: column.DisplayOrder ?? index + 1,
                        visibility: column.visibility !== false && column.checked !== false,
                        checked: column.visibility !== false && column.checked !== false,
                    })));
                }
            })
            .catch((error) => console.error("Unable to load column preferences:", error.message))
            .finally(() => {
                if (!cancelled) setDemandColumnsReady(true);
            });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const email = getUserEmail();
        if (!email) return undefined;
        let cancelled = false;
        getColumnPreferences(email, "PV Insights Page")
            .then((response) => {
                const columns = response?.columns || response?.default_columns;
                if (!cancelled && Array.isArray(columns) && columns.length) {
                    setPvColumns(columns.map((column, index) => ({
                        ...column,
                        key: column.key || column.label,
                        DisplayOrder: column.DisplayOrder ?? index + 1,
                        visibility: column.visibility !== false && column.checked !== false,
                        checked: column.visibility !== false && column.checked !== false,
                    })));
                }
            })
            .catch((error) => console.error("Unable to load PV column preferences:", error.message));
        return () => { cancelled = true; };
    }, []);

    const filterType = metric === "pv" ? "PV Insights Page" : "Demand Page";
    const activeFilters = metric === "pv" ? pvFilters : demandFilters;
    const setActiveFilters = metric === "pv" ? setPvFilters : setDemandFilters;
    const activeColumns = metric === "pv" ? pvColumns : demandColumns;

    const handleFilterApply = (_filterKey, _nextValues, _orderedOptions, mergedValues) => {
        if (mergedValues && typeof mergedValues === "object") {
            // Check if there are any selected filters
            const hasSelection = Object.values(mergedValues).some((value) => {
                if (Array.isArray(value)) return value.length > 0;
                if (typeof value === 'string') return value.trim().length > 0;
                return value !== undefined && value !== null;
            });

            clearFiltersRef.current = !hasSelection;
            setActiveFilters(mergedValues);
        }
    };

    const handleExport = async () => {
        if (exportStatus) return;

        const exportLabel = metric === "demand" ? "Demand data" : "Product Version data";
        const exportTable = metric === "demand" ? exportDemandTable : exportProductVisionTable;
        setExportStatus(`${exportLabel} is exporting`);

        let exported = false;
        try {
            exported = await exportTable();
        } catch (error) {
            console.error("Unable to export dashboard data:", error.message);
        }
        if (!exported) {
            setExportStatus(null);
            return;
        }

        setExportStatus(`${exportLabel} is exported`);
        exportStatusTimeoutRef.current = setTimeout(() => {
            setExportStatus(null);
            exportStatusTimeoutRef.current = null;
        }, 3000);
    };

        // handle table view change SAP APO or DFOS
        const handleUOMValue = useCallback((e, value) => {
            setUom(value);
        }, []);
    

    return (
        <Box sx={{ height: `calc(100vh - 51px)`, overflow: "auto", position: "relative" }}>
            {!revealed && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fff",
                        zIndex: 10,
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
            <Box sx={{ visibility: revealed ? "visible" : "hidden" }}>
                <Filter
                    filterType={filterType}
                    values={activeFilters}
                    onApply={handleFilterApply}
                    onRefresh={(cleared) => {
                        clearFiltersRef.current = true;
                        setActiveFilters(cleared || {});
                    }}
                    onLoadingChange={handleFilterLoading}
                    onColumnApply={(columns) => {
                        if (metric === "demand") setDemandColumns(columns);
                        else setPvColumns(columns);
                    }}
                    columnPreferences={activeColumns}
                />
                <Box component="main" className={styles.page}>
                    <Box component="section" className={styles.card}>
                        <Box className={styles.toolbar}>
                            {metric === "demand" && (
                                <h1 className={styles.title}>Demand on BG Level</h1>
                            )}

                            <Box className={styles.controls}>
                                <TalViewTabs value={uom} onChange={handleUOMValue} aria-label="table tabs">
                                    <TalViewTab label="Default" value={"Default"} style={{ height: '40px' }} />
                                    <TalViewTab label="Tonnes" value={"Tonnes"} style={{ height: '40px' }} />
                                </TalViewTabs>
                                {metric === "demand" && (
                                    <>
                                        {/* <Dropdown
                                        label=""
                                        placeholder="select Site Cluster"
                                        width="196px"
                                        options={SITE_CLUSTER_OPTIONS}
                                    /> */}

                                        <Dropdown
                                            label="Site Code/Cluster"
                                            placeholder="Select Site Code"
                                            width="196px"
                                            options={SITE_CODE_OPTIONS}
                                            value={siteCodeSelection}
                                            onApply={(nextValues) => setSiteCodeSelection(nextValues)}
                                            autoApply
                                            disabled={!isSiteColumnVisible}
                                        />

                                        <Dropdown
                                            label="BG Levels"
                                            placeholder="All"
                                            width="196px"
                                            options={BG_LEVEL_OPTIONS}
                                            value={bgLevels}
                                            onApply={(nextValues) => setBgLevels(nextValues)}
                                            disabled={!isBgColumnVisible}
                                        />
                                    </>
                                )}

                                <Box className={styles.segmentedControl} aria-label="Metric">
                                    <button
                                        className={`${styles.segmentButton} ${metric === "demand" ? styles.segmentButtonActive : ""}`}
                                        type="button"
                                        onClick={() => setMetric("demand")}
                                    >
                                        Demand
                                    </button>
                                    <button
                                        className={`${styles.segmentButton} ${metric === "pv" ? styles.segmentButtonActive : ""}`}
                                        type="button"
                                        onClick={() => setMetric("pv")}
                                    >
                                        Production version (PV)
                                    </button>
                                </Box>

                                <Tooltip
                                    title={exportStatus || ""}
                                    open={Boolean(exportStatus)}
                                    placement="top-start"
                                    slotProps={{
                                        tooltip: { sx: { color: "#000", fontSize: "14px" } },
                                    }}
                                >
                                    <span>
                                        <button
                                            className={styles.exportButton}
                                            type="button"
                                            onClick={handleExport}
                                            disabled={Boolean(exportStatus)}
                                        >
                                            <Box src={XLSIcon} alt='XLS' component='img' />
                                            Export
                                        </button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>

                        {metric === "demand" ? (
                            <DemandTable
                                filters={activeFilters}
                                sublevelFilters={sublevelFilters}
                                clearFilters={clearFiltersRef.current}
                                columnPreferences={demandColumns}
                                columnPreferencesReady={demandColumnsReady}
                                showSiteCode={showSiteCode}
                                uom={uom}
                            />
                        ) : (
                            <ProductVisionTable filters={activeFilters} clearFilters={clearFiltersRef.current} columnPreferences={pvColumns} />
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default RmpmDashboard;
